import * as zod from 'zod';

import { CartesianPose, CartesianPosition } from '@sb/geometry';
import {
  CalibrationCoverageResult,
  CameraIntegration,
  CameraIntrinsics,
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
  ExtrinsicsType,
  ChessboardParameters,
} from '@sb/integrations/types/cameraTypes';
import { three } from '@sb/utilities';

import { Space } from '../types';

import {
  Blob2D,
  Detection2D,
  Blob2DParams,
  Detection3DPoseEstimationConfig,
  DinoFastParams,
  Extents3D,
  LocateMatch,
  LocateMethod,
  OrientationSource,
  ProjectionMethodEnum,
  ResultOrdering,
  Shape2DParams,
  TemplateImage,
  TextPromptDetection2DParams,
} from './LocateTypes';
import {
  CalculateIntrinsicsDeprecatedResult,
  ChessboardCornersResult,
  CalibrationEntry,
  ClassifyClass,
  ClassifyResult,
  RegionOfInterest,
} from './VisionInterface';

/* classify */

const RunClassifyArgs = zod.object({
  method: zod.literal('classify'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  classes: ClassifyClass.array(),
});

const RunClassifyResult = zod.object({
  method: zod.literal('classify'),
  results: ClassifyResult.array(),
});

/* classify (DinoFast) */

// Carries per-example embeddings on the wire so the runner can score against
// them without re-fetching the user's locally-encoded examples. Stale
// embeddings (where `modelVersion` ≠ `embeddingModelVersion`) are lazily
// re-encoded on the runner side from `storageID`.
const RunClassifyDinoFastExample = zod.object({
  storageID: zod.string(),
  embedding: zod.object({
    values: zod.array(zod.number()),
    modelVersion: zod.string(),
  }),
});

const RunClassifyDinoFastClass = zod.object({
  name: zod.string(),
  examples: zod.array(RunClassifyDinoFastExample),
});

const RunClassifyDinoFastArgs = zod.object({
  method: zod.literal('classifyDinoFast'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  classes: zod.array(RunClassifyDinoFastClass),
  embeddingModelVersion: zod.string(),
  precision: zod.string(),
  gpuTaskRunnerId: zod.string(),
});

export type RunClassifyDinoFastArgs = zod.infer<typeof RunClassifyDinoFastArgs>;

const RunClassifyDinoFastResult = zod.object({
  method: zod.literal('classifyDinoFast'),
  results: ClassifyResult.array(),
});

/* detect 2d blobs */

const RunDetect2DBlobsArgs = zod.object({
  method: zod.literal('detect2DBlobs'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: Blob2DParams,
  resultOrdering: ResultOrdering.optional(),
});

export type RunDetect2DBlobsArgs = zod.infer<typeof RunDetect2DBlobsArgs>;

const RunDetect2DBlobsResult = zod.object({
  method: zod.literal('detect2DBlobs'),
  results: LocateMatch.array(),
});

/* detect 2d shapes */

const RunDetect2DShapesArgs = zod.object({
  method: zod.literal('detect2DShapes'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: Shape2DParams,
  templateImage: TemplateImage,
  resultOrdering: ResultOrdering.optional(),
});

export type RunDetect2DShapesArgs = zod.infer<typeof RunDetect2DShapesArgs>;

const RunDetect2DShapesResult = zod.object({
  method: zod.literal('detect2DShapes'),
  results: LocateMatch.array(),
});

/* detect 2d text prompts */

const RunDetect2DTextPromptArgs = zod.object({
  method: zod.literal('detect2DTextPrompt'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: TextPromptDetection2DParams,
  resultOrdering: ResultOrdering.optional(),
});

export type RunDetect2DTextPromptArgs = zod.infer<
  typeof RunDetect2DTextPromptArgs
>;

const RunDetect2DTextPromptResult = zod.object({
  method: zod.literal('detect2DTextPrompt'),
  results: LocateMatch.array(),
});

/* detect 2d DinoFast */

// Preview-only: runs the encoder + heatmap + connected components on a single
// frame and returns 2D bounding boxes. The full Locate pipeline (plane
// projection, pose estimation) is invoked separately via `runVisionMethod
// ({ method: 'locate' })`.
const RunDetect2DDinoFastArgs = zod.object({
  method: zod.literal('detect2DDinoFast'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: DinoFastParams,
  resultOrdering: ResultOrdering.optional(),
  // Teach-preview only: also return the dense score heatmap the detections
  // were thresholded from, so the teach UI's heatmap overlay tracks the live
  // frame through the regular detection polling. Routine execution and the
  // projection/results previews leave it off — the payload is dead weight
  // there.
  returnHeatmap: zod.boolean().optional(),
});

export type RunDetect2DDinoFastArgs = zod.infer<typeof RunDetect2DDinoFastArgs>;

// The dense per-patch score map a DinoFast detection pass was thresholded
// from — the learned head's sigmoid scores when a head is set, the cosine
// similarity scores otherwise. Same grid + letterbox semantics as the
// TrainLocateHead response heatmap.
export const DinoFastDenseHeatmap = zod.object({
  // IEEE-754 half-precision little-endian row-major scores (gridH * gridW),
  // base64-encoded because typed arrays don't survive JSON-over-WS.
  valuesFp16Base64: zod.string(),
  gridH: zod.number(),
  gridW: zod.number(),
  // Encoder letterbox geometry: the grid covers a square `inputW × inputH`
  // canvas with the (ROI-cropped) image centered between the padding bands.
  inputW: zod.number(),
  inputH: zod.number(),
  paddingTop: zod.number(),
  paddingLeft: zod.number(),
  modelVersion: zod.string(),
  // Placement of the scored region within the source frame, in source-image
  // pixels — the grid covers only the ROI crop, not the full frame. Equals
  // the full frame when no ROI crop applied.
  crop: zod.object({
    x: zod.number(),
    y: zod.number(),
    width: zod.number(),
    height: zod.number(),
  }),
});

export type DinoFastDenseHeatmap = zod.infer<typeof DinoFastDenseHeatmap>;

const RunDetect2DDinoFastResult = zod.object({
  method: zod.literal('detect2DDinoFast'),
  results: LocateMatch.array(),
  // Present only when the request set `returnHeatmap`.
  heatmap: DinoFastDenseHeatmap.optional(),
});

/* detect custom checkpoint 2d */

const RunDetectCustomCheckpoint2DArgs = zod.object({
  method: zod.literal('detectCustomCheckpoint2D'),
  camera: CameraIntegration.optional(),
  // Optional equipment ID for camera-bot-cpp mode (when set, camera field is ignored)
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  locateMethod: LocateMethod,
  resultOrdering: ResultOrdering.optional(),
});

export type RunDetectCustomCheckpoint2DArgs = zod.infer<
  typeof RunDetectCustomCheckpoint2DArgs
>;

const RunDetectCustomCheckpoint2DResult = zod.object({
  method: zod.literal('detectCustomCheckpoint2D'),
  results: LocateMatch.array(),
});

/* get chessboard corners */

const RunGetChessboardCornersArgs = zod.object({
  method: zod.literal('getChessboardCorners'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  chessboard: ChessboardParameters,
});

const RunGetChessboardCornersResult = zod.object({
  method: zod.literal('getChessboardCorners'),
  results: ChessboardCornersResult,
});

/* get_camera_chessboard_transform */

const RunGetCameraChessboardTransformArgs = zod.object({
  method: zod.literal('getCameraChessboardTransform'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  chessboard: ChessboardParameters,
});

const RunGetCameraChessboardTransformResult = zod.object({
  method: zod.literal('getCameraChessboardTransform'),
  results: CartesianPose,
});

/* locate */

const RunLocateArgs = zod.object({
  method: zod.literal('locate'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  locateMethod: LocateMethod,
  plane: zod.tuple(three(CartesianPosition)).optional(),
  resultsLimit: zod.number(),
  resultOrdering: ResultOrdering.optional(),
  transform: CartesianPose.nullable(),
  // Grasp frame the `transform` offset rides in (object / plane / world).
  // Carried over the wire so the live-preview path applies the
  // same frame the saved skill will. Optional + omitted defaults to today's
  // pose-frame behavior in runLocate.
  graspFrame: zod.enum(['object', 'plane', 'world']).optional(),
  // Canonical orientation frame from teach time (when reliable). Rides into
  // the plane-projection request so vision-bot can resolve the 180° flip in
  // the object grasp frame. Null/omitted ⇒ vision-bot falls back to plane.
  orientationReference: zod
    .object({
      referenceContour: zod.array(zod.array(zod.number()).length(2)),
    })
    .nullable()
    .optional(),
  accuracyCalibration: Space.AccuracyCalibrationEntry.array().nullable(),
  objectHeight: zod.number().default(0),
  intrinsics: CameraIntrinsics.optional(),
  extrinsics: ExtrinsicsKinematics.optional(),
  projectionMethod: ProjectionMethodEnum.optional(),
  ransacParams: zod
    .object({
      maxIterations: zod.number().optional(),
      distanceThreshold: zod.number().optional(),
      minSamples: zod.number().optional(),
    })
    .optional(),
  maxTiltAngle: zod.number().min(0).max(90).optional(),
  minInlierRatio: zod.number().min(0).max(1).optional(),
  fallbackToTopDown: zod.boolean().optional(),
  disablePlaneOrientation: zod.boolean().optional(),
  // DinoFast preview only: also return the dense score heatmap the 2D
  // detections were thresholded from, so the routine editor's ClickFind Locate
  // step shows the same heatmap overlay as the Teach page. Routine execution
  // leaves it off — the payload is dead weight there.
  returnHeatmap: zod.boolean().optional(),
  // Required when `projectionMethod` is 'Detection3D' — which is a projection,
  // distinct from `locateMethod.kind === 'Detection3D'` (a 3D detection
  // *method*, with its own 2D pass).
  detection3dConfig: Detection3DPoseEstimationConfig.optional(),
});

export const RunLocateResult = zod.object({
  method: zod.literal('locate'),
  results: zod
    .object({
      blob: Detection2D,
      pose: CartesianPose,
      // Mirrors `LocateResult.poseBeforeOffset` — where this match sat before
      // the skill's offset moved it. Preserved on the wire so a preview can
      // draw both ends of the offset rather than re-deriving the near end from
      // the configured numbers. Absent when the pose was never offset.
      poseBeforeOffset: CartesianPose.optional(),
      // Mirrors `LocateResult.extents` from runLocate — preserved on the wire
      // so callers (e.g. the locate wizard's Results step) can render the
      // depth-derived OBB indicator alongside each detection's pose.
      extents: Extents3D.optional(),
      // Mirrors `LocateResult.segment` — the SAM2 contour echoed back from
      // vision-bot's plane-projection response. Null when segmentation was off.
      segment: Blob2D.nullable().default(null),
      // Mirrors `LocateResult.usedTopDownFallback` — Geometric plane detection
      // could not trust the surface fit, so this pose is a top-down guess.
      // Preserved on the wire so the preview can flag it per match.
      usedTopDownFallback: zod.boolean().optional(),
      // Mirrors `LocateResult.orientationSource` — where this match's in-plane
      // grasp orientation came from. Preserved on the wire so the preview can
      // flag a match whose orientation missed the taught template.
      orientationSource: OrientationSource.optional(),
    })
    .array(),
  debugVisualization: zod.string().optional(),
  // Present only when the request set `returnHeatmap` (DinoFast preview). The
  // dense score heatmap from the 2D-detection pass that fed plane projection,
  // so the ClickFind Locate step can render the heatmap overlay.
  heatmap: DinoFastDenseHeatmap.optional(),
});

export type RunLocateResult = zod.infer<typeof RunLocateResult>;

// Re-exported so callers of this module don't also need to reach into
// LocateTypes for the enum that names `RunLocateResult.results[].orientationSource`.
export { OrientationSource };

/* compute orientation reference */

// Teach-time only: establish a canonical orientation frame from a taught
// contour. The runner forwards the contour to vision-bot's
// ComputeOrientationReference RPC and maps the normalized reference contour
// back. An unusable shape returns isReliable=false with an empty
// referenceContour, and the frontend persists nothing.
const Contour2D = zod.array(zod.object({ x: zod.number(), y: zod.number() }));

// Why no reference came back. The remedies differ — a symmetric part can never
// carry an object frame, an inconsistent scene usually means the segmentation
// needs better lighting or a tighter ROI before re-teaching, and
// `pcaSufficient` needs nothing at all: the part is 180-degree symmetric but
// elongated, so each detection's own principal axis already gives the same
// answer a reference could, and the object frame still resolves at run time.
// The UI has to be able to say which one happened.
export const OrientationReferenceRejection = zod.enum([
  'symmetric',
  'sceneInconsistent',
  'pcaSufficient',
]);

export type OrientationReferenceRejection = zod.infer<
  typeof OrientationReferenceRejection
>;

const RunComputeOrientationReferenceArgs = zod.object({
  method: zod.literal('computeOrientationReference'),
  // Taught-instance contour in image-pixel space (the primary match segment).
  contour: Contour2D,
  // The teach scene's OTHER matches, same space as `contour`. vision-bot keeps
  // the reference only if every one registers to the primary — a scene whose
  // own instances disagree has contours too noisy to resolve an object frame
  // from. Omitted or empty (single-match scene) preserves the old behaviour.
  siblingContours: zod.array(Contour2D).optional(),
});

export type RunComputeOrientationReferenceArgs = zod.infer<
  typeof RunComputeOrientationReferenceArgs
>;

const RunComputeOrientationReferenceResult = zod.object({
  method: zod.literal('computeOrientationReference'),
  isReliable: zod.boolean(),
  // Normalized reference contour as [x, y] pairs; empty when not reliable.
  referenceContour: zod.array(zod.array(zod.number())),
  // Set only when isReliable is false; absent when vision-bot reported no
  // recognizable reason.
  rejection: OrientationReferenceRejection.nullable().optional(),
  // The scene gate's evidence: how many of the teach scene's other matches
  // registered against the primary, out of how many were testable. Absent
  // from an older vision-bot. Not persisted on the skill — they describe the
  // teach scene that produced the template, so a saved count would go stale.
  registeredSiblings: zod.number().nullable().optional(),
  checkedSiblings: zod.number().nullable().optional(),
});

export type RunComputeOrientationReferenceResult = zod.infer<
  typeof RunComputeOrientationReferenceResult
>;

/* get intrinsics */

const GetIntrinsicsArgs = zod.object({
  method: zod.literal('getIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
});

const GetIntrinsicsResult = zod.object({
  method: zod.literal('getIntrinsics'),
  results: zod.union([CameraIntrinsics, CameraIntrinsicsExtended]),
});

/* get built-in intrinsics */

const GetBuiltInIntrinsicsArgs = zod.object({
  method: zod.literal('getBuiltInIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
});

const GetBuiltInIntrinsicsResult = zod.object({
  method: zod.literal('getBuiltInIntrinsics'),
  // Extended so a camera's self-reported lens survives to the caller. The two
  // distortion fields default to empty, so a camera that reports no model
  // parses to exactly the previous shape.
  results: CameraIntrinsicsExtended,
});

/* calculate intrinsics (deprecated) */

const RunCalculateIntrinsicsDeprecatedArgs = zod.object({
  method: zod.literal('calculateIntrinsicsDeprecated'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
});

const RunCalculateIntrinsicsDeprecatedResult = zod.object({
  method: zod.literal('calculateIntrinsicsDeprecated'),
  results: CalculateIntrinsicsDeprecatedResult,
});

/* calibrate camera intrinsics */
const RunCalibrateCameraIntrinsicsArgs = zod.object({
  method: zod.literal('calibrateCameraIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
});

const RunCalibrateCameraIntrinsicsResult = zod.object({
  method: zod.literal('calibrateCameraIntrinsics'),
  results: CameraIntrinsicsExtended,
});

/* calibrate camera extrinsics */
const RunCalibrateCameraExtrinsicsArgs = zod.object({
  method: zod.literal('calibrateCameraExtrinsics'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
  intrinsics: CameraIntrinsicsExtended,
  extrinsicsType: ExtrinsicsType,
});

const RunCalibrateCameraExtrinsicsResult = zod.object({
  method: zod.literal('calibrateCameraExtrinsics'),
  results: CartesianPose,
});

/* assess calibration coverage */
const RunAssessCalibrationCoverageArgs = zod.object({
  method: zod.literal('assessCalibrationCoverage'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
  calibrationType: zod.enum(['intrinsic', 'extrinsic']),
  intrinsics: CameraIntrinsicsExtended.optional(),
  // Extrinsics only: which mount the views were captured for, so the backend
  // can score coverage against the right motion model.
  mountType: ExtrinsicsType.optional(),
});

const RunAssessCalibrationCoverageResult = zod.object({
  method: zod.literal('assessCalibrationCoverage'),
  results: CalibrationCoverageResult,
});

/* detect scene plane */

export const ScenePlaneCandidateZod = zod.object({
  planePose: CartesianPose, // base frame, canonical axes (design doc §4.4)
  surfaceNormal: CartesianPosition,
  inlierRatio: zod.number(),
  inlierCount: zod.number(),
  rmsErrorM: zod.number(),
  tiltAngleDeg: zod.number(),
  medianDepthM: zod.number().optional(),
  inlierMask: zod
    .object({
      rleBase64: zod.string(),
      width: zod.number(),
      height: zod.number(),
    })
    .optional(),
});
export type ScenePlaneCandidate = zod.infer<typeof ScenePlaneCandidateZod>;

// Per-click outline of the valid-depth pixels that click sampled, so the UI
// can show users what each click contributes to the fit. One per accepted
// seed; clicks with no usable depth have none.
export const SeedWindowZod = zod.object({
  seedIndex: zod.number(),
  contourPoints: zod.object({ x: zod.number(), y: zod.number() }).array(), // boundary tracing the sampled pixels, intrinsic px
});
export type SeedWindow = zod.infer<typeof SeedWindowZod>;

const RunDetectScenePlaneArgs = zod.object({
  method: zod.literal('detectScenePlane'),
  camera: CameraIntegration.optional(), // legacy wrist path
  cameraEquipmentId: zod.string().optional(), // useCppCameraBot path
  seedPoints: zod.object({ x: zod.number(), y: zod.number() }).array(), // clicked, intrinsic px
  ransacParams: zod
    .object({
      maxIterations: zod.number().optional(),
      distanceThreshold: zod.number().optional(), // meters at 1 m (adaptive scaling)
      fixedThreshold: zod.boolean().optional(),
    })
    .optional(),
  depthBand: zod.object({ minM: zod.number(), maxM: zod.number() }).optional(),
  quality: zod.enum(['preview', 'final']).default('preview'), // preview: stride 8; final: stride 4 + full-res refit
  useCachedFrame: zod.boolean().optional(), // re-fits against the frozen pair (design §4.7)
  includeInlierMask: zod.boolean().default(true),
});

export type RunDetectScenePlaneArgs = zod.infer<typeof RunDetectScenePlaneArgs>;

const RunDetectScenePlaneResult = zod.object({
  method: zod.literal('detectScenePlane'),
  results: zod.object({
    candidates: ScenePlaneCandidateZod.array(),
    ignoredSeedIndices: zod.number().array(),
    seedWindows: SeedWindowZod.array(),
    processingTimeMs: zod.number().optional(),
  }),
});

export type RunDetectScenePlaneResult = zod.infer<
  typeof RunDetectScenePlaneResult
>;

/* union */

export const RunVisionMethodArgs = zod.discriminatedUnion('method', [
  RunClassifyArgs,
  RunClassifyDinoFastArgs,
  RunDetect2DBlobsArgs,
  RunDetect2DShapesArgs,
  RunDetect2DTextPromptArgs,
  RunDetect2DDinoFastArgs,
  RunDetectCustomCheckpoint2DArgs,
  RunGetChessboardCornersArgs,
  RunGetCameraChessboardTransformArgs,
  RunLocateArgs,
  RunComputeOrientationReferenceArgs,
  GetIntrinsicsArgs,
  GetBuiltInIntrinsicsArgs,
  RunCalculateIntrinsicsDeprecatedArgs,
  RunCalibrateCameraIntrinsicsArgs,
  RunCalibrateCameraExtrinsicsArgs,
  RunAssessCalibrationCoverageArgs,
  RunDetectScenePlaneArgs,
]);

export type RunVisionMethodArgs = zod.infer<typeof RunVisionMethodArgs>;

export const RunVisionMethodResult = zod.discriminatedUnion('method', [
  RunClassifyResult,
  RunClassifyDinoFastResult,
  RunDetect2DBlobsResult,
  RunDetect2DShapesResult,
  RunDetect2DTextPromptResult,
  RunDetect2DDinoFastResult,
  RunDetectCustomCheckpoint2DResult,
  RunGetChessboardCornersResult,
  RunGetCameraChessboardTransformResult,
  RunLocateResult,
  RunComputeOrientationReferenceResult,
  GetIntrinsicsResult,
  GetBuiltInIntrinsicsResult,
  RunCalculateIntrinsicsDeprecatedResult,
  RunCalibrateCameraIntrinsicsResult,
  RunCalibrateCameraExtrinsicsResult,
  RunAssessCalibrationCoverageResult,
  RunDetectScenePlaneResult,
]);

export type RunVisionMethodResult = zod.infer<typeof RunVisionMethodResult>;

/* compute dino embeddings */

// Image is base64-encoded to survive JSON transport. The receiver decodes it
// back to bytes before forwarding to vision-bot.
export const ComputeDinoEmbeddingsArgs = zod.object({
  imageBase64: zod.string(),
  modelVersion: zod.string(),
  precision: zod.string(),
  gpuTaskRunnerId: zod.string(),
});

export type ComputeDinoEmbeddingsArgs = zod.infer<
  typeof ComputeDinoEmbeddingsArgs
>;

/* compute dino image embedding */

// Same wire shape as ComputeDinoEmbeddingsArgs — only the response differs
// (single CLS vector vs full patch grid). Kept as a sibling schema so the
// two methods have distinct types and don't accidentally cross at call sites.
export const ComputeDinoImageEmbeddingArgs = zod.object({
  imageBase64: zod.string(),
  modelVersion: zod.string(),
  precision: zod.string(),
  gpuTaskRunnerId: zod.string(),
});

export type ComputeDinoImageEmbeddingArgs = zod.infer<
  typeof ComputeDinoImageEmbeddingArgs
>;

/* segment with sam2 (DinoFast High-precision mask preview) */

// Image is base64-encoded to survive JSON transport. The receiver decodes it
// back to bytes before forwarding to vision-bot.
export const SegmentMaskArgs = zod.object({
  imageBase64: zod.string(),
  boxes: zod.array(
    zod.object({
      x1: zod.number(),
      y1: zod.number(),
      x2: zod.number(),
      y2: zod.number(),
      // Optional SAM2 point prompts (positives on the object, negatives on
      // holes / in-box background). Missing/empty ⇒ box-only prompt.
      points: zod
        .array(
          zod.object({
            x: zod.number(),
            y: zod.number(),
            positive: zod.boolean(),
          }),
        )
        .optional(),
    }),
  ),
  gpuTaskRunnerId: zod.string(),
  // Segmentation backend; defaults to 'sam2' server-side. The CPU
  // 'bilateral_solver' method refines the dense DINO heatmap below instead of
  // running SAM2.
  method: zod
    .enum(['sam2', 'bilateral_solver', 'heatmap_threshold'])
    .optional(),
  // Dense fp16 heatmap, base64-encoded because typed arrays don't survive
  // JSON-over-WS. Same pixel space as `imageBase64` and `boxes`. The
  // bilateral_solver method consumes it as its input; SAM2 uses it to gate
  // masks to the heatmap's foreground support.
  heatmapDenseBase64: zod.string().optional(),
  heatmapWidth: zod.number().optional(),
  heatmapHeight: zod.number().optional(),
  heatmapThreshold: zod.number().optional(),
  // SAM2-only: per-patch heatmap samples for multimask selection (see
  // deriveHeatmapSamples). Missing/empty ⇒ single-mask behavior.
  heatmapSamples: zod
    .array(
      zod.object({
        x: zod.number(),
        y: zod.number(),
        score: zod.number(),
      }),
    )
    .optional(),
});

export type SegmentMaskArgs = zod.infer<typeof SegmentMaskArgs>;

/* train locate head (ClickFind teach-time head training) */

// Image is base64-encoded to survive JSON transport. The receiver decodes it
// back to bytes; `callTrainLocateHead` maps `headKind` onto the proto
// `LocateHeadKind` enum before forwarding to vision-bot.
export const TrainLocateHeadArgs = zod.object({
  imageBase64: zod.string(),
  modelVersion: zod.string(),
  precision: zod.string(),
  gpuTaskRunnerId: zod.string(),
  sessionId: zod.string(),
  seq: zod.number(),
  clicks: zod.array(
    zod.object({
      nx: zod.number(),
      ny: zod.number(),
      positive: zod.boolean(),
    }),
  ),
  committedPositives: zod.array(
    zod.object({ values: zod.array(zod.number()) }),
  ),
  committedNegatives: zod.array(
    zod.object({ values: zod.array(zod.number()) }),
  ),
  headKind: zod.enum(['mlp', 'logistic']),
});

export type TrainLocateHeadArgs = zod.infer<typeof TrainLocateHeadArgs>;

/* preload a Roboflow workflow (Inspect and Locate import-time warming) */

/**
 * Warms a workflow on whichever vision-bot will run it.
 *
 * `gpuTaskRunnerId` may be empty, which means the robot's own vision-bot. That
 * is the whole reason this exists as a routine-runner message rather than a
 * `GPUTaskRunnerCommand`: an equipment command needs an equipment id, and a
 * robot with no runner has none to give.
 *
 * No API key. The robot reads its own; the browser never holds one.
 */
export const PreloadRoboflowWorkflowArgs = zod.object({
  gpuTaskRunnerId: zod.string(),
  workspaceName: zod.string(),
  workflowId: zod.string(),
});

export type PreloadRoboflowWorkflowArgs = zod.infer<
  typeof PreloadRoboflowWorkflowArgs
>;
